/* Ayudate · Alta directa de cuenta de estudiante
   Página de registro abierta: no depende de ningún enlace de invitación
   (esa cadena de "el tutor confirmó → correo con token → elegir usuario y
   contraseña" ya no existe). Nombre, correo, teléfono y credenciales se
   llenan todos acá mismo, y de ahí en más se entra por /login — igual que
   FormTutor.jsx para los tutores. Completamente aparte de reservar una
   tutoría en Agenda.jsx, que sigue siendo anónimo. */

import React, { useState } from "react";
import { Marca } from "../componentes/Marca";
import { ESTILOS_MINIMOS } from "../estilos/minimos";
import { api, ErrorApi } from "../api";
import { irA_ruta } from "../lib/rutas";
import { Ayuda, DisponibilidadUsuario } from "../componentes/Mensajes";
import { TelefonoInput } from "../componentes/TelefonoInput";
import { CampoPassword } from "../componentes/CampoPassword";
import { vNombreCompleto, vCorreo, vTelefono, vUsuario, vPassword, sanitizarTexto } from "../lib/validar";
import { useDisponibilidadUsuario } from "../lib/disponibilidadUsuario";
import { CODIGO_PAIS_PREDETERMINADO } from "../datos/codigosPais";

const MENSAJES_ERROR = {
  CORREO_OCUPADO: "Ya existe una cuenta con ese correo. Inicie sesión en vez de crear una nueva.",
  USUARIO_OCUPADO: "Ese usuario ya lo tiene otra persona. Pruebe con otro.",
};

export function PaginaCrearCuentaEstudiante() {
  const [nombre, setNombre] = useState("");
  const [correo, setCorreo] = useState("");
  const [codigoTel, setCodigoTel] = useState(CODIGO_PAIS_PREDETERMINADO);
  const [telefono, setTelefono] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmar, setConfirmar] = useState("");
  const disponibilidadUsuario = useDisponibilidadUsuario(username);
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);
  const [error, setError] = useState("");
  const [intento, setIntento] = useState(false);

  const msg = {
    nombre: vNombreCompleto(nombre),
    correo: vCorreo(correo),
    telefono: vTelefono(telefono),
    usuario: vUsuario(username) || (disponibilidadUsuario === "ocupado" ? "Ese usuario ya existe." : ""),
    password: vPassword(password),
    confirmar: password !== confirmar ? "Las contraseñas no coinciden." : "",
  };
  const falta = Object.fromEntries(Object.entries(msg).map(([k, v]) => [k, !!v]));
  const completo = !Object.values(falta).some(Boolean);
  const ay = (k) => ({ error: msg[k], mostrar: intento });
  const cl = (k) => (intento && falta[k] ? "falta" : "");

  const enviar = async (e) => {
    e.preventDefault();
    if (!completo) { setIntento(true); return; }

    setEnviando(true); setError("");
    try {
      await api.registrarEstudiante({
        nombre: sanitizarTexto(nombre), correo: sanitizarTexto(correo),
        telefono: `${codigoTel} ${telefono}`, usuario: sanitizarTexto(username), password,
      });
      setListo(true);
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      setError(MENSAJES_ERROR[codigo] || err.message || "No pudimos crear su cuenta. Pruebe de nuevo.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="v2">
      <style>{ESTILOS_MINIMOS}</style>
      <div className="mini-wrap">
        <div className="logo" style={{ marginBottom: 28 }}><Marca /></div>

        {listo ? (
          <div className="mini-card">
            <h1>Su cuenta ya está lista</h1>
            <p>Ya puede entrar a su panel de estudiante con el usuario y la contraseña que eligió.</p>
            <a className="btn btn-p" href="/login"
              onClick={(e) => { e.preventDefault(); irA_ruta("/login"); }}>
              Ir a iniciar sesión
            </a>
          </div>
        ) : (
          <form className="mini-card" onSubmit={enviar}>
            <h1>Cree su cuenta de estudiante</h1>
            <p>Llene sus datos y elija un usuario y una contraseña para ver sus reuniones y solicitar
               nuevas tutorías desde su propio panel.</p>

            <label className="lb" htmlFor="nombre">Nombre completo</label>
            <input id="nombre" className={`in ${cl("nombre")}`} value={nombre} autoComplete="name"
              onChange={(e) => setNombre(e.target.value)} placeholder="Su nombre y apellido" />
            <Ayuda texto="Nombre y apellido." {...ay("nombre")} />

            <label className="lb" htmlFor="correo">Correo</label>
            <input id="correo" className={`in ${cl("correo")}`} type="email" value={correo}
              autoComplete="email" onChange={(e) => setCorreo(e.target.value)}
              placeholder="correo@ejemplo.com" />
            <Ayuda texto="Ahí le avisamos sobre sus tutorías." {...ay("correo")} />

            <label className="lb" htmlFor="telefono">Teléfono</label>
            <TelefonoInput id="telefono" className={cl("telefono")} codigo={codigoTel}
              onCodigoChange={setCodigoTel} valor={telefono} onValorChange={setTelefono}
              ariaInvalid={intento && falta.telefono} />
            <Ayuda texto="Para coordinar sus tutorías por WhatsApp si hace falta." {...ay("telefono")} />

            <label className="lb" htmlFor="username">Usuario</label>
            <input id="username" className={`in ${cl("usuario")}`} value={username}
              autoComplete="username" onChange={(e) => setUsername(e.target.value)}
              placeholder="Su usuario" />
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
              <Ayuda texto="Con este usuario entra después a su panel." {...ay("usuario")} />
              <DisponibilidadUsuario estado={disponibilidadUsuario} />
            </div>

            <label className="lb" htmlFor="password">Contraseña</label>
            <CampoPassword id="password" className={`in ${cl("password")}`} value={password}
              autoComplete="new-password" onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 8 caracteres" />
            <Ayuda texto="Mínimo 8 caracteres." {...ay("password")} />

            <label className="lb" htmlFor="confirmar">Confirmar contraseña</label>
            <CampoPassword id="confirmar" className={`in ${cl("confirmar")}`} value={confirmar}
              autoComplete="new-password" onChange={(e) => setConfirmar(e.target.value)}
              placeholder="Repítala" />
            <Ayuda texto="Escríbala de nuevo para confirmar." {...ay("confirmar")} />

            {error && <div className="error" role="alert">{error}</div>}

            <button className="btn btn-p" style={{ width: "100%", marginTop: 16 }}
              disabled={enviando}>
              {enviando ? "Creando cuenta…" : "Crear cuenta"}
            </button>

            <a className="btn" href="/login" style={{ width: "100%", textAlign: "center", marginTop: 10 }}
              onClick={(e) => { e.preventDefault(); irA_ruta("/login"); }}>
              Ya tengo cuenta
            </a>
          </form>
        )}
      </div>
    </div>
  );
}
